import { Quaternion, Vector2, Vector3 } from "three";

// Physically based lighting of the Earth's surface under the atmosphere,
// keyed to real optical depths (the same values as atmosphere-shader.js):
//   - sunlight reaching the ground is attenuated by the air along the sun
//     ray, so it reddens and fades towards the terminator;
//   - blue skylight adds diffuse irradiance on the day side;
//   - what we see is attenuated by the air between the surface and the
//     camera (aerial perspective, per colour); the atmosphere shell adds
//     the inscattered light on top;
//   - city lights are masked to the night side, fading in through twilight;
//   - cloud shadows fall where the sun places them (offset by sun angle);
//   - the night side is moonlit (see MOONLIGHT_GLSL).
// Optical depth through an exponential atmosphere uses Schüler's
// approximation of the Chapman function.

// Vertical optical depths at sea level, and planet radius / scale height.
export const RAYLEIGH_DEPTH = [0.044, 0.104, 0.179];
export const MIE_DEPTH = 0.028; // extinction (scattering / 0.9)
export const OZONE_DEPTH = [0.0098, 0.028, 0.0013];
export const RAYLEIGH_X = 6371 / 8;
export const MIE_X = 6371 / 1.2;

// Cloud cover from a cloud map texel: the static map (JPEG) has it in its
// colour, the live map (clouds-alpha.png) in alpha over white, so a x g
// works for both.
//
// cloudViewWindow thins the clouds (and their shadows) over land around the
// point the camera looks at, so the land in view stays readable from orbit;
// n and the camera position are world space, land is 0 (sea) to 1. CLOUD_MAX_ALPHA caps thick cloud so
// coastlines show faintly through it everywhere. `strength` scales the
// clearing: it opens onto the news, so with the news off (None) it closes.
export const CLOUD_MAX_ALPHA = 0.8;
export const CLOUD_WINDOW_FLOOR = 0.0;
// Thin the clouds over land only (landCover); off, they thin over sea too.
export const CLOUD_WINDOW_LAND_ONLY = false;
export const CLOUD_COVER_GLSL = `
float cloudCover(vec4 texel) { return texel.a * texel.g; }
float cloudViewWindow(vec3 n, vec3 camPos, float land, float strength) {
  float d = distance(normalize(n), normalize(camPos));
  return mix(1.0, mix(${CLOUD_WINDOW_FLOOR.toFixed(2)}, 1.0, smoothstep(0.27, 0.85, d)), land * strength);
}
// Land (1) or sea (0) from the water map, read at fixed mips (whatever the
// zoom) and remapped so the coast counts as land: the clearing reaches a
// little way out to sea (256 texels wide, ~1.4 degrees a texel), then the
// clouds return over a longer tail (64 wide, ~5.6 degrees a texel).
float landCover(sampler2D water, vec2 uv) {
  if (!${CLOUD_WINDOW_LAND_ONLY}) return 1.0;
  float lod = max(0.0, log2(float(textureSize(water, 0).x) / 256.0));
  float nearCoast = smoothstep(0.02, 0.5, 1.0 - textureLod(water, uv, lod).r);
  float tail = smoothstep(0.0, 0.6, 1.0 - textureLod(water, uv, lod + 2.0).r);
  return max(nearCoast, tail);
}
`;

export const ATMOSPHERE_GLSL = `
const vec3 RAYLEIGH_DEPTH = vec3(${RAYLEIGH_DEPTH.join(", ")});
const float MIE_DEPTH = ${MIE_DEPTH};
const vec3 OZONE_DEPTH = vec3(${OZONE_DEPTH.join(", ")});
const float RAYLEIGH_X = ${RAYLEIGH_X.toFixed(2)};
const float MIE_X = ${MIE_X.toFixed(2)};

// Relative optical depth (air mass) from the ground towards a direction at
// zenith cosine c, for planet radius / scale height X (Schüler 2012).
float chapman(float X, float c) {
  float s = sqrt(X);
  if (c >= 0.0) return s / (s * c + 1.0);
  if (c < -0.25) return 1e4; // well below the horizon: no light gets through
  float x0 = sqrt(1.0 - c * c) * X;
  return 2.0 * sqrt(x0) * exp(X - x0) - s / (1.0 - s * c);
}

// Transmittance through the whole atmosphere from the ground along zenith cosine c.
vec3 atmosphereTransmittance(float c) {
  vec3 depth = (RAYLEIGH_DEPTH + OZONE_DEPTH) * chapman(RAYLEIGH_X, c) + MIE_DEPTH * chapman(MIE_X, c);
  return exp(-min(depth, vec3(60.0)));
}
`;

// UV of a unit object-space direction under SphereGeometry's mapping (u
// along longitude, v from the south pole), and of the point `arc` radians
// from n along the unit tangent h. For marching across a texture over the
// sphere. Literal pi: prepended before three's <common> chunk defines PI.
export const SPHERE_UV_GLSL = `
vec2 sphereUv(vec3 n) {
  float theta = acos(clamp(n.y, -1.0, 1.0));
  return vec2(fract(atan(n.z, -n.x) / 6.28318531), 1.0 - theta / 3.14159265);
}
vec2 sphereUvToward(vec3 n, vec3 h, float arc) {
  return sphereUv(normalize(n * cos(arc) + h * sin(arc)));
}
`;

// Moonlight on the night side, as night vision sees it. Real moonlight is
// ~1/400,000 of sunlight, far too faint for the exposure, so it's raised to
// a readable level but stays one light: from the Moon's real direction,
// scaled by its phase, so full-moon nights are brighter than new-moon ones
// (which keep a faint floor). The eye has no colour vision that dim, so it's
// grey-blue (a scotopic tint of luminance 1), and it only applies once the
// sun has set, where the exaggerated moonlight can't compete with daylight.
// Needs uMoonDir, uMoonIrradiance, uNightFloor and ATMOSPHERE_GLSL.
export const MOONLIGHT_GLSL = `
const vec3 SCOTOPIC_TINT = vec3(0.84, 1.01, 1.35);
vec3 moonIrradiance(vec3 n, float sunNdl) {
  float moonNdl = dot(n, uMoonDir);
  float moon = uMoonIrradiance * max(moonNdl, 0.0) * atmosphereTransmittance(moonNdl).g;
  return (moon + uNightFloor) * SCOTOPIC_TINT * smoothstep(0.02, -0.15, sunNdl);
}
`;

// Phase law of a Lambertian sphere: 1 at full moon, 1/pi at quarter, 0 at new.
function moonPhase(moonDir, sunDir) {
  const alpha = Math.acos(Math.max(-1, Math.min(1, -moonDir.dot(sunDir)))); // phase angle
  return ((Math.PI - alpha) * Math.cos(alpha) + Math.sin(alpha)) / Math.PI;
}

// Scene irradiance of the full moon and the moonless-night floor (in the
// sun's units, 2.0), picked for readability under the night exposure.
export const FULL_MOON_IRRADIANCE = 0.1;
export const NIGHT_FLOOR_IRRADIANCE = 0.012;

export function moonUniforms() {
  return {
    uMoonDir: { value: new Vector3(0, 1, 0) },
    uMoonIrradiance: { value: 0.0 },
    uNightFloor: { value: NIGHT_FLOOR_IRRADIANCE },
  };
}

export function setMoon(uniforms, moonDir, sunDir) {
  uniforms.uMoonDir.value.copy(moonDir).normalize();
  uniforms.uMoonIrradiance.value = FULL_MOON_IRRADIANCE * moonPhase(uniforms.uMoonDir.value, sunDir.clone().normalize());
}

// Terrain shadows: topology map value v -> height ~ 8.8 km x v^1.7 (fitted
// to Everest, Tibet, Denver and the Sahara), exaggerated 5x since at 20 km
// per texel real relief would barely show. Traced ~190 km towards the sun,
// only with the sun below ~7 degrees, where real shadows grow long enough to see. The edge is
// softer than the sun's disc (0.27 degrees) would make it, to hide the
// coarse height map's texels.
const TERRAIN_TOP = (8.8 / 6371) * 5;
const TERRAIN_MARCH = 0.03;
const TERRAIN_PENUMBRA = 0.015;

export function applyEarthLighting(material, { clouds, water, night, earthMesh, cloudAltitude = 0.002, cloudShadow = 0.35 } = {}) {
  const uniforms = {
    ...moonUniforms(),
    uWater: { value: water || null },
    // For cloud-shader.js: city lights seen through clouds, and the sun in
    // the (shared) object space of the Earth and cloud meshes.
    uNight: { value: night || null },
    // Seasons (seasons.js): the material's map is one month, this the next,
    // mixed by uSeasonBlend.
    uSeasonNext: { value: null },
    uSeasonBlend: { value: 0.0 },
    // Zoomed-in detail (detail-patches.js): coarse and fine patches, their
    // rectangles (west longitude, north latitude, span in degrees), which
    // are on, and the global map's degrees per texel.
    uDetail0: { value: null },
    uDetail1: { value: null },
    uDetailRect0: { value: new Vector3() },
    uDetailRect1: { value: new Vector3() },
    uDetailOn: { value: new Vector2() },
    uBaseTexelDeg: { value: 360 / 2048 },
    uCityRadiance: { value: 0.14 },
    uSunDir: { value: new Vector3(1, 0, 0) },       // world space
    uSunDirObject: { value: new Vector3(1, 0, 0) }, // earth mesh's object space (for cloud-shadow UVs)
    uSunIrradiance: { value: 2.0 },
    uClouds: { value: clouds || null },
    // Cross-fade from the previous cloud map: coverage is blended from
    // uCloudsPrev to the current map as uCloudsMix runs 0 → 1.
    uCloudsPrev: { value: null },
    uCloudsMix: { value: 1.0 },
    uCloudShadow: { value: clouds ? cloudShadow : 0.0 },
    // How far the clouds clear around the view (0 with the news off).
    uCloudWindow: { value: 1.0 },
    uCloudAltitude: { value: cloudAltitude },
  };
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = `varying vec3 vEarthWorldNormal;
varying vec3 vEarthObjectNormal;
varying vec3 vEarthWorldPosition;
` + shader.vertexShader
      .replace("#include <beginnormal_vertex>", `#include <beginnormal_vertex>
  vEarthWorldNormal = normalize(mat3(modelMatrix) * objectNormal);
  vEarthObjectNormal = normalize(objectNormal);`)
      .replace("#include <worldpos_vertex>", `#include <worldpos_vertex>
  vEarthWorldPosition = (modelMatrix * vec4(transformed, 1.0)).xyz;`);
    shader.fragmentShader = `varying vec3 vEarthWorldNormal;
varying vec3 vEarthObjectNormal;
varying vec3 vEarthWorldPosition;
uniform vec3 uSunDir;
uniform vec3 uSunDirObject;
uniform float uSunIrradiance;
uniform sampler2D uClouds;
uniform sampler2D uCloudsPrev;
uniform float uCloudsMix;
uniform float uCloudShadow;
uniform float uCloudWindow;
uniform float uCloudAltitude;
uniform sampler2D uWater;
uniform sampler2D uSeasonNext;
uniform float uSeasonBlend;
uniform sampler2D uDetail0;
uniform sampler2D uDetail1;
uniform vec3 uDetailRect0;
uniform vec3 uDetailRect1;
uniform vec2 uDetailOn;
uniform float uBaseTexelDeg;

// Position in a detail patch (u east, v north, 0-1) of a point at
// (longitude, latitude), and how far inside it is (fading over its edge).
vec2 detailPatchUv(vec3 rect, vec2 lngLat, out float inside) {
  float u = fract((lngLat.x - rect.x) / 360.0) * 360.0 / rect.z;
  float v = (rect.y - lngLat.y) / rect.z;
  inside = smoothstep(0.0, 0.06, min(min(u, 1.0 - u), min(v, 1.0 - v)));
  return vec2(u, 1.0 - v);
}

// Zoomed-in detail on the seasonal colour: the patches' fine structure
// (detail / its blur at the global map's resolution, from the coarse patch)
// multiplies the colour, so the season stays even though the tiles are
// one month.
vec3 applyDetail(vec3 colour, vec2 mapUv) {
  vec2 lngLat = vec2(mapUv.x * 360.0 - 180.0, mapUv.y * 180.0 - 90.0);
  float inCoarse, inFine;
  vec2 coarseUv = detailPatchUv(uDetailRect0, lngLat, inCoarse);
  vec2 fineUv = detailPatchUv(uDetailRect1, lngLat, inFine);
  inFine *= uDetailOn.y;
  float blurLod = log2(uBaseTexelDeg / (uDetailRect0.z / float(textureSize(uDetail0, 0).x)));
  vec3 blur = textureLod(uDetail0, coarseUv, blurLod).rgb;
  vec3 detail = mix(texture2D(uDetail0, coarseUv).rgb, texture2D(uDetail1, fineUv).rgb, inFine);
  vec3 ratio = clamp(detail / max(blur, vec3(0.02)), 0.25, 4.0);
  return mix(colour, colour * ratio, inCoarse);
}
uniform vec3 uMoonDir;
uniform float uMoonIrradiance;
uniform float uNightFloor;
${ATMOSPHERE_GLSL}
${SPHERE_UV_GLSL}
${MOONLIGHT_GLSL}
${CLOUD_COVER_GLSL}

// UV of the cloud that shadows this point: the cloud layer's point towards
// the sun, offset horizontally by altitude / cos(zenith). Same mapping as
// SphereGeometry (u along longitude, v from the south pole).
vec2 cloudShadowUv(vec3 n, vec3 s) {
  float theta = acos(clamp(n.y, -1.0, 1.0));
  float phi = atan(n.z, -n.x);
  vec3 east = vec3(sin(phi), 0.0, cos(phi));
  vec3 north = vec3(cos(phi) * cos(theta), sin(theta), -sin(phi) * cos(theta));
  float su = max(dot(s, n), 0.08);
  float offset = min(uCloudAltitude / su, 0.03);
  float dPhi = dot(s, east) * offset / max(sin(theta), 0.05);
  float dTheta = -dot(s, north) * offset;
  // Literal pi: this is prepended before three's <common> chunk defines PI.
  return vec2(fract((phi + dPhi) / 6.28318531), 1.0 - (theta + dTheta) / 3.14159265);
}
` + shader.fragmentShader
      .replace("#include <map_fragment>", `#include <map_fragment>
  #ifdef USE_MAP
    if (uSeasonBlend > 0.0) diffuseColor.rgb = mix(diffuseColor.rgb, diffuse * texture2D(uSeasonNext, vMapUv).rgb, uSeasonBlend);
    if (uDetailOn.x > 0.5) diffuseColor.rgb = applyDetail(diffuseColor.rgb, vMapUv);
  #endif
  float earthNdl = dot(normalize(vEarthWorldNormal), uSunDir);`)
      .replace("#include <emissivemap_fragment>", `#include <emissivemap_fragment>
  // City lights come on as the sun sets and are full by nautical twilight.
  totalEmissiveRadiance *= smoothstep(0.05, -0.2, earthNdl);`)
      .replace("#include <lights_fragment_end>", `#include <lights_fragment_end>
  {
    // Direct sunlight arrives through the atmosphere, and clouds shade it.
    vec3 sunTransmittance = atmosphereTransmittance(earthNdl);
    float shadow = 1.0;
    #ifdef USE_MAP
      vec2 shadowUv = cloudShadowUv(normalize(vEarthObjectNormal), normalize(uSunDirObject));
      float shadowCover = cloudCover(texture2D(uClouds, shadowUv));
      if (uCloudsMix < 1.0) shadowCover = mix(cloudCover(texture2D(uCloudsPrev, shadowUv)), shadowCover, uCloudsMix);
      shadow = 1.0 - uCloudShadow * shadowCover * cloudViewWindow(vEarthWorldNormal, cameraPosition, landCover(uWater, vMapUv), uCloudWindow);
    #endif
    #ifdef USE_BUMPMAP
      if (earthNdl > -0.03 && earthNdl < 0.12) {
        vec3 on = normalize(vEarthObjectNormal);
        vec3 os = normalize(uSunDirObject);
        float sinE = dot(on, os);
        vec3 towardSun = os - on * sinE;
        float horizontal = length(towardSun);
        if (horizontal > 1e-4) {
          towardSun /= horizontal;
          float lod = log2(max(length(dFdx(vBumpMapUv)), length(dFdy(vBumpMapUv))) * float(textureSize(bumpMap, 0).x));
          #define TERRAIN_HEIGHT(uv) (${TERRAIN_TOP.toFixed(6)} * pow(textureLod(bumpMap, uv, lod).r, 1.7))
          float h0 = TERRAIN_HEIGHT(vBumpMapUv);
          float horizon = -1.0;
          for (int i = 1; i <= 16; i++) {
            float arc = ${TERRAIN_MARCH} * pow(float(i) / 16.0, 1.5);
            horizon = max(horizon, (TERRAIN_HEIGHT(sphereUvToward(on, towardSun, arc)) - h0) / arc);
          }
          float elevation = atan(sinE, horizontal);
          float lit = smoothstep(-${TERRAIN_PENUMBRA}, ${TERRAIN_PENUMBRA}, elevation - atan(horizon));
          shadow *= mix(1.0, lit, smoothstep(0.12, 0.06, earthNdl));
        }
      }
    #endif
    vec3 direct = sunTransmittance * shadow;
    reflectedLight.directDiffuse *= direct;
    reflectedLight.directSpecular *= direct;
    // Skylight: the sunlight the air scatters out of the beam, about half of
    // it downwards, spread over the sky (blue, fading through twilight).
    vec3 skyIrradiance = uSunIrradiance * (1.0 - atmosphereTransmittance(1.0)) * 0.5
                       * smoothstep(-0.12, 0.3, earthNdl) * max(earthNdl + 0.2, 0.0);
    reflectedLight.indirectDiffuse += skyIrradiance * diffuseColor.rgb * RECIPROCAL_PI;
    // Moonlight, on a grey night albedo: land and sea at fixed levels, with
    // the day map's brightness compressed (sqrt) so deserts don't outshine
    // forests the way they do in colour daylight.
    #ifdef USE_MAP
      float nightLum = dot(diffuseColor.rgb, vec3(0.2126, 0.7152, 0.0722));
      float nightAlbedo = mix(0.08 + 0.22 * sqrt(nightLum), 0.03, texture2D(uWater, vMapUv).r);
      reflectedLight.indirectDiffuse += moonIrradiance(normalize(vEarthWorldNormal), earthNdl) * nightAlbedo * RECIPROCAL_PI;
    #endif
  }`)
      .replace("#include <opaque_fragment>", `
  // Aerial perspective: the air between surface and camera dims what we see.
  float earthNdv = dot(normalize(vEarthWorldNormal), normalize(cameraPosition - vEarthWorldPosition));
  outgoingLight *= atmosphereTransmittance(max(earthNdv, 0.0));
  #include <opaque_fragment>`);
  };
  material.customProgramCacheKey = () => "earth-lighting-v7";
  material.needsUpdate = true;

  const inverseRotation = new Quaternion();
  return {
    setSunDirection(dir) {
      uniforms.uSunDir.value.copy(dir).normalize();
      if (earthMesh) {
        inverseRotation.copy(earthMesh.quaternion).invert();
        uniforms.uSunDirObject.value.copy(uniforms.uSunDir.value).applyQuaternion(inverseRotation);
      }
    },
    setSunIrradiance(value) { uniforms.uSunIrradiance.value = value; },
    setMoon(moonDir, sunDir) { setMoon(uniforms, moonDir, sunDir); },
    uniforms,
  };
}

// Cloud tops up to ~12 km (as a fraction of the radius), and how far
// (radians of arc, ~130 km) cloud shadows are traced towards the sun.
// Cover maps are smooth where real convective tops are steep, so the relief
// slope is exaggerated and height rises with density squared (thick cores
// tower, thin cloud stays low).
const CLOUD_THICKNESS = 12 / 6371;
const CLOUD_RELIEF_GAIN = 3.0;
const CLOUD_MARCH = 0.02;

// Clouds under the same physics, for cloud-shader.js (needs
// vVertexWorldPosition, vCloudObjectNormal, map, uSunDir, uSunDirObject,
// uNight, uCityRadiance, ATMOSPHERE_GLSL, SPHERE_UV_GLSL,
// CLOUD_COVER_GLSL and MOONLIGHT_GLSL; cloudEmission is added as emission): lit by transmitted
// sunlight (pink-orange tops at sunset), dark at night, brighter when
// backlit (forward scattering, Henyey-Greenstein g = 0.6, normalised so it
// averages ~1), and seen through the air in front of them.
// Thickness: the cover map is read as cloud-top height, so at low sun tops
// are lit on their sunward slopes (relief) and shade the cloud beyond them
// (a short march towards the sun). At night clouds glow softly with the
// city lights beneath them, blurred, instead of blocking them.
export const CLOUD_LIGHTING_GLSL = `
  vec3 cloudNormal = normalize(vVertexWorldPosition);
  float cloudNdl = dot(cloudNormal, uSunDir);
  vec3 cloudSun = atmosphereTransmittance(cloudNdl) * smoothstep(-0.05, 0.08, cloudNdl);
  // Mip level of a texture of this width at this pixel (the maps vary: 2k
  // or 4k, and live clouds).
  float cloudUvRate = max(length(dFdx(vMapUv)), length(dFdy(vMapUv)));
  float cloudLod = log2(cloudUvRate * float(textureSize(map, 0).x));
  #ifdef USE_MAP
  if (cloudNdl > -0.05 && cloudNdl < 0.7) {
    vec3 on = normalize(vCloudObjectNormal);
    vec3 os = normalize(uSunDirObject);
    float sinE = dot(on, os);
    vec3 towardSun = os - on * sinE;
    float horizontal = length(towardSun);
    if (horizontal > 1e-4) {
      towardSun /= horizontal;
      const float H = ${CLOUD_THICKNESS.toFixed(6)};
      float TEXEL = 6.28318531 / float(textureSize(map, 0).x);
      #define CLOUD_TOP(uv, lod) (pow(cloudCoverAt(uv, lod), 2.0) * H)
      float h0 = CLOUD_TOP(vMapUv, cloudLod);
      // Relief: slope of the tops along the sun's direction.
      float ahead = CLOUD_TOP(sphereUvToward(on, towardSun, 1.5 * TEXEL), cloudLod);
      float behind = CLOUD_TOP(sphereUvToward(on, towardSun, -1.5 * TEXEL), cloudLod);
      float slope = ${CLOUD_RELIEF_GAIN.toFixed(1)} * (ahead - behind) / (3.0 * TEXEL);
      float relief = clamp((sinE - slope * horizontal) / max(sinE, 0.05), 0.2, 1.8);
      // Self-shadow: how far cloud towards the sun rises above the sun ray.
      float tanE = max(sinE, 0.02) / horizontal;
      float occlusion = 0.0;
      for (int i = 1; i <= 8; i++) {
        float arc = ${CLOUD_MARCH} * pow(float(i) / 8.0, 1.5);
        float h = CLOUD_TOP(sphereUvToward(on, towardSun, arc), cloudLod + 1.0);
        occlusion += max(0.0, h - (h0 + arc * tanE));
      }
      // Shadowed cloud still gets light scattered in from around it.
      float shade = mix(0.35, 1.0, exp(-1.5 * occlusion / H));
      // Faded out towards higher sun, where it's switched off.
      cloudSun *= mix(1.0, relief * shade, smoothstep(0.7, 0.55, cloudNdl));
    }
  }
  #endif
  vec3 cloudViewDir = normalize(cameraPosition - vVertexWorldPosition);
  float cloudCos = dot(-cloudViewDir, uSunDir);
  float hg = (1.0 - 0.36) / pow(1.0 + 0.36 - 1.2 * cloudCos, 1.5);
  vec3 cloudTint = cloudSun * (0.85 + 0.15 * hg);
  cloudTint *= atmosphereTransmittance(max(dot(cloudNormal, cloudViewDir), 0.0));
  vec3 cloudMoon = moonIrradiance(cloudNormal, cloudNdl) * 0.6 * RECIPROCAL_PI
                 * atmosphereTransmittance(max(dot(cloudNormal, cloudViewDir), 0.0)).g;
  // City light passing up through the cloud, spread over ~16 texels.
  vec3 cityThroughCloud = uCityRadiance * 0.8 * smoothstep(0.05, -0.2, cloudNdl)
                        * textureLod(uNight, vMapUv, log2(cloudUvRate * float(textureSize(uNight, 0).x)) + 4.0).rgb;
  vec3 cloudEmission = cloudMoon + cityThroughCloud;
`;
